import React from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

/**
 * MetricCard - KPI card with optional sparkline and trend indicator
 */
export function MetricCard({ 
  label, 
  value, 
  subtitle, 
  trend, 
  sparklineData = [], 
  color = '#007bff',
  onClick,
  loading = false 
}) {
  const trendIcon = trend > 0 ? '↗' : trend < 0 ? '↘' : '→';
  const trendColor = trend > 0 ? '#28a745' : trend < 0 ? '#dc3545' : '#6c757d';

  return (
    <div 
      onClick={onClick}
      style={{ 
        background: 'var(--card-bg)', 
        border: '1px solid var(--border-light)', 
        borderLeft: `4px solid ${color}`,
        borderRadius: '12px', 
        padding: '1rem', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.5rem', 
        boxShadow: 'var(--shadow-sm)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        minHeight: '120px'
      }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.transform = 'none', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
    >
      <div style={{ fontSize: '.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        {label}
      </div>
      
      {loading ? (
        <div style={{ height: '2rem', background: 'var(--border-light)', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
      ) : (
        <>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>
            {value}
          </div>
          
          {trend != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '.75rem' }}>
              <span style={{ color: trendColor, fontWeight: 600 }}>
                {trendIcon} {Math.abs(trend).toFixed(1)}%
              </span>
              <span style={{ color: 'var(--text-muted)' }}>vs prev period</span>
            </div>
          )}
          
          {subtitle && (
            <div style={{ fontSize: '.75rem', color: 'var(--text-secondary)' }}>
              {subtitle}
            </div>
          )}
          
          {sparklineData.length > 0 && (
            <div style={{ marginTop: 'auto', height: '30px', marginLeft: '-0.5rem', marginRight: '-0.5rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={color} 
                    strokeWidth={2} 
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * LoadingChart - Skeleton placeholder for charts
 */
export function LoadingChart({ height = 300 }) {
  return (
    <div style={{ width: '100%', height, display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem' }}>
      <div style={{ height: '20px', background: 'var(--border-light)', borderRadius: '4px', width: '40%', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ flex: 1, background: 'var(--border-light)', borderRadius: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
    </div>
  );
}

/**
 * EmptyState - No data message
 */
export function EmptyState({ message = 'No data available', icon = '📊' }) {
  return (
    <div style={{ 
      padding: '3rem 1rem', 
      textAlign: 'center', 
      color: 'var(--text-muted)',
      background: 'var(--card-bg)',
      borderRadius: '12px',
      border: '2px dashed var(--border-light)'
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.5 }}>{icon}</div>
      <div style={{ fontSize: '.9rem', fontWeight: 500 }}>{message}</div>
    </div>
  );
}

/**
 * ChartLegend - Custom legend with toggles
 */
export function ChartLegend({ items = [], onToggle }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', padding: '0.5rem 0' }}>
      {items.map((item, idx) => (
        <div 
          key={idx}
          onClick={() => onToggle && onToggle(item.key)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            cursor: onToggle ? 'pointer' : 'default',
            opacity: item.hidden ? 0.5 : 1,
            transition: 'opacity 0.2s'
          }}
        >
          <div style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '2px', 
            background: item.color,
            opacity: item.hidden ? 0.3 : 1
          }} />
          <span style={{ fontSize: '.75rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
            {item.label} {item.value != null && `(${item.value})`}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * DateRangePicker - Consistent date selector with presets
 */
export function DateRangePicker({ from, to, onChange, presets = true }) {
  const handlePreset = (days) => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - days * 86400000);
    onChange({ from: startDate.toISOString(), to: endDate.toISOString() });
  };

  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {presets && (
        <>
          <button className="secondary" onClick={() => handlePreset(1)}>24h</button>
          <button className="secondary" onClick={() => handlePreset(7)}>7d</button>
          <button className="secondary" onClick={() => handlePreset(30)}>30d</button>
          <button className="secondary" onClick={() => handlePreset(90)}>90d</button>
          <div style={{ width: '1px', height: '24px', background: 'var(--border-light)', margin: '0 0.25rem' }} />
        </>
      )}
      <input 
        type="datetime-local" 
        value={from ? new Date(from).toISOString().slice(0, 16) : ''}
        onChange={(e) => onChange({ from: e.target.value ? new Date(e.target.value).toISOString() : '', to })}
        style={{ fontSize: '.75rem' }}
      />
      <span style={{ color: 'var(--text-muted)' }}>to</span>
      <input 
        type="datetime-local" 
        value={to ? new Date(to).toISOString().slice(0, 16) : ''}
        onChange={(e) => onChange({ from, to: e.target.value ? new Date(e.target.value).toISOString() : '' })}
        style={{ fontSize: '.75rem' }}
      />
    </div>
  );
}

/**
 * ExportButton - Download data as CSV/JSON
 */
export function ExportButton({ data, filename = 'export', format = 'csv' }) {
  const handleExport = () => {
    let content = '';
    let mimeType = '';
    
    if (format === 'csv') {
      if (!data || data.length === 0) return;
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
      ].join('\n');
      content = csv;
      mimeType = 'text/csv';
    } else if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button className="secondary" onClick={handleExport} disabled={!data || data.length === 0}>
      ⬇ Export {format.toUpperCase()}
    </button>
  );
}

/**
 * InsightCard - Highlight anomalies, patterns, opportunities
 */
export function InsightCard({ title, description, severity = 'info', icon }) {
  const colors = {
    info: { bg: '#e3f2fd', border: '#2196f3', text: '#0d47a1' },
    success: { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20' },
    warning: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
    danger: { bg: '#ffebee', border: '#f44336', text: '#b71c1c' }
  };
  const color = colors[severity] || colors.info;
  const defaultIcon = severity === 'warning' ? '⚠️' : severity === 'danger' ? '🚨' : severity === 'success' ? '✅' : '💡';

  return (
    <div style={{ 
      background: color.bg, 
      border: `1px solid ${color.border}`, 
      borderRadius: '8px', 
      padding: '0.75rem 1rem',
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-start'
    }}>
      <div style={{ fontSize: '1.25rem', flexShrink: 0 }}>{icon || defaultIcon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '.8rem', fontWeight: 600, color: color.text, marginBottom: '0.25rem' }}>
          {title}
        </div>
        <div style={{ fontSize: '.75rem', color: color.text, opacity: 0.9 }}>
          {description}
        </div>
      </div>
    </div>
  );
}

/**
 * Heatmap - Activity heatmap (7 days × 24 hours grid)
 */
export function ActivityHeatmap({ data = [], max = 100 }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const getColor = (value) => {
    if (!value || value === 0) return 'var(--border-light)';
    const intensity = Math.min(value / max, 1);
    const hue = 200;
    const lightness = 90 - (intensity * 40);
    return `hsl(${hue}, 70%, ${lightness}%)`;
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'inline-grid', gridTemplateColumns: '40px repeat(24, 18px)', gap: '2px', minWidth: '600px' }}>
        <div></div>
        {hours.map(h => (
          <div key={h} style={{ fontSize: '.6rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            {h}
          </div>
        ))}
        {days.map((day, dayIdx) => (
          <React.Fragment key={day}>
            <div style={{ fontSize: '.7rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
              {day}
            </div>
            {hours.map(hour => {
              const dataPoint = data.find(d => d.day === dayIdx && d.hour === hour);
              const value = dataPoint?.value || 0;
              return (
                <div 
                  key={hour}
                  title={`${day} ${hour}:00 - ${value} events`}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    background: getColor(value),
                    borderRadius: '2px',
                    cursor: 'pointer',
                    border: '1px solid var(--border-light)'
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * ProgressBar - Simple progress indicator
 */
export function ProgressBar({ value, max, label, color = '#007bff', height = '8px' }) {
  const percentage = Math.min((value / max) * 100, 100);
  
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '.75rem' }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value.toLocaleString()} / {max.toLocaleString()}</span>
        </div>
      )}
      <div style={{ 
        width: '100%', 
        height, 
        background: 'var(--border-light)', 
        borderRadius: height, 
        overflow: 'hidden' 
      }}>
        <div style={{ 
          width: `${percentage}%`, 
          height: '100%', 
          background: color,
          transition: 'width 0.3s ease',
          borderRadius: height
        }} />
      </div>
    </div>
  );
}

// Add pulse animation to global styles if not exists
const style = document.createElement('style');
style.innerHTML = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
if (!document.querySelector('style[data-analytics-animations]')) {
  style.setAttribute('data-analytics-animations', 'true');
  document.head.appendChild(style);
}

// Re-export PDF components for convenience
export { default as PdfExportButton } from './PdfExportButton';
export { ReportGenerator } from '../../utils/ReportGenerator';
